import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

interface Document {
  id: number;
  type: string;
  nom: string;
  description?: string;
  extension: string;
  taille: number;
  actif: boolean;
  created_at: string;
  updated_at: string;
}

interface DocumentsContextType {
  commonDocuments: Document[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const DocumentsContext = createContext<DocumentsContextType | undefined>(undefined);

export const DocumentsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [commonDocuments, setCommonDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async (forceRefresh = false) => {
    // ⚡ Si déjà chargés et pas de refresh forcé, ne pas recharger (cache)
    if (commonDocuments.length > 0 && !forceRefresh) {
      return;
    }

    // ⚡ Ne pas appeler l'API si pas de token (utilisateur non connecté)
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/documents-communs?per_page=100`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json'
        },
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const json = await response.json();

      const docs = Array.isArray(json?.data?.data)
        ? json.data.data
        : Array.isArray(json?.data)
          ? json.data
          : [];

      // La bibliographie acheteur est réservée à l'onglet Profil > Documents
      setCommonDocuments(docs.filter((d: any) => d.type !== 'bibliographie_acheteur'));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      console.error('Erreur lors du chargement des documents communs:', err);
    } finally {
      setLoading(false);
    }
  }, [commonDocuments.length]);

  const refetch = async () => {
    setCommonDocuments([]); // Vider le cache
    await fetchDocuments(true);
  };

  // ⚡ Écouter les changements de token dans localStorage (connexion/déconnexion)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchDocuments();
    } else {
      // Si déconnecté, vider les documents
      setCommonDocuments([]);
    }
  }, [fetchDocuments]);

  return (
    <DocumentsContext.Provider value={{ commonDocuments, loading, error, refetch }}>
      {children}
    </DocumentsContext.Provider>
  );
};

export const useDocuments = (): DocumentsContextType => {
  const context = useContext(DocumentsContext);
  if (!context) {
    throw new Error('useDocuments doit être utilisé dans un DocumentsProvider');
  }
  return context;
};
